/**
 * Global Search Component
 *
 * Universal search input with autocomplete that searches users by:
 * - Telegram ID (numeric query)
 * - Username (text query with partial matching)
 * - Auth code (6-character alphanumeric)
 *
 * Features:
 * - Debounced search (300ms delay)
 * - Autocomplete dropdown with formatted results
 * - Result selection callback
 * - Loading and empty states
 * - Keyboard navigation support
 * - Russian language interface
 *
 * Requirements:
 * - 4.1-4.8: Universal search functionality with autocomplete
 */

import React, { useState, useCallback, useMemo } from "react";
import { AutoComplete, Typography, Space, Tag, Spin } from "antd";
import type { AutoCompleteProps } from "antd";
import {
  SearchOutlined,
  UserOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import { UsersService } from "../api-client";
import type { SearchResult } from "../api-client";

const { Text } = Typography;

/**
 * Props for the GlobalSearch component
 */
interface GlobalSearchProps {
  /**
   * Callback fired when a user is selected from the search results
   * @param userId - The telegram_id of the selected user
   */
  onSelect: (userId: number) => void;

  /**
   * Optional placeholder text (defaults to Russian)
   */
  placeholder?: string;

  /**
   * Optional width of the search input
   */
  width?: number | string;
}

/**
 * Option type for AutoComplete
 */
interface SearchOption {
  value: string; // telegram_id as string for AutoComplete
  label: React.ReactNode;
  data: SearchResult;
}

/**
 * GlobalSearch component with debounced autocomplete
 *
 * Leverages Ant Design AutoComplete and lodash debounce for optimal UX.
 */
export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  onSelect,
  placeholder = "Поиск: ID / @username / код",
  width = 400,
}) => {
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  /**
   * Format search result into AutoComplete option
   */
  const formatSearchResult = (result: SearchResult): SearchOption => {
    const usernameDisplay = result.username
      ? `@${result.username}`
      : "Без username";

    return {
      value: result.telegram_id.toString(),
      data: result,
      label: (
        <Space
          direction="vertical"
          size={4}
          style={{ width: "100%", padding: "4px 0" }}
        >
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Space>
              <UserOutlined style={{ color: "#1890ff" }} />
              <Text strong>{usernameDisplay}</Text>
              <Text type="secondary">({result.telegram_id})</Text>
            </Space>
            <Tag
              icon={
                result.match_type === "exact" ? (
                  <CheckCircleOutlined />
                ) : undefined
              }
              color={result.match_type === "exact" ? "success" : "default"}
            >
              {result.match_type === "exact" ? "Точное" : "Частичное"}
            </Tag>
          </Space>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Баланс: <Text strong>{result.balance}</Text>
          </Text>
        </Space>
      ),
    };
  };

  /**
   * Perform search via API
   */
  const performSearch = async (query: string) => {
    if (!query || query.trim().length === 0) {
      setOptions([]);
      return;
    }

    setLoading(true);

    try {
      const response = await UsersService.searchUsersApiUsersSearchGet({
        q: query.trim(),
        limit: 10,
      });

      if (response.results && response.results.length > 0) {
        const formattedOptions = response.results.map(formatSearchResult);
        setOptions(formattedOptions);
      } else {
        // Show "no results" option
        setOptions([
          {
            value: "",
            label: (
              <Text type="secondary" style={{ padding: "8px 0" }}>
                Пользователи не найдены по запросу "{query}"
              </Text>
            ),
            data: {} as SearchResult,
          },
        ]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setOptions([
        {
          value: "",
          label: (
            <Text type="danger" style={{ padding: "8px 0" }}>
              Ошибка поиска. Попробуйте снова.
            </Text>
          ),
          data: {} as SearchResult,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Debounced search function (300ms delay)
   */
  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        performSearch(query);
      }, 300),
    []
  );

  /**
   * Handle search input change
   */
  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);

      if (!value || value.trim().length === 0) {
        setOptions([]);
        return;
      }

      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  /**
   * Handle user selection from dropdown
   */
  const handleSelect: AutoCompleteProps["onSelect"] = (_value, option) => {
    const selectedOption = option as SearchOption;

    // Check if it's a valid result (not the "no results" option)
    if (selectedOption.data && selectedOption.data.telegram_id) {
      onSelect(selectedOption.data.telegram_id);
      // Clear the search input after selection
      setSearchValue("");
      setOptions([]);
    }
  };

  /**
   * Handle dropdown visibility change
   */
  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open) {
      // Clear options when dropdown closes
      setOptions([]);
    }
  };

  return (
    <AutoComplete
      value={searchValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      placeholder={placeholder}
      style={{ width }}
      size="large"
      allowClear
      notFoundContent={
        loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="small" />
            <Text
              type="secondary"
              style={{ display: "block", marginTop: "8px" }}
            >
              Поиск...
            </Text>
          </div>
        ) : null
      }
      suffixIcon={<SearchOutlined style={{ color: "#1890ff" }} />}
    />
  );
};

export default GlobalSearch;
